import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { useAuth } from "../../../context/AuthContext";
import { useCart } from "../../../context/CartContext";
import { useConfirm } from "../../../components/common/ConfirmDialog";
import { api } from "../../../lib/api";
import { fmtMoney } from "../../../lib/format";
import { errorDetail, useDelivery, useShop } from "../hooks";
import { CartLine } from "../cart/CartLine";
import { DeliveryPicker } from "../cart/DeliveryPicker";
import { OrderPlaced } from "../cart/OrderPlaced";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";
import { unitPrice } from "../../../lib/uom";
import { offerFor, useOffers } from "../../../lib/offers";

const Row = ({ label, value, strong, testId }) => (
  <div className={`flex justify-between ${strong ? "text-base font-bold text-navy" : "text-sm text-slate-600"}`}>
    <span>{label}</span><span className="tabular-nums" data-testid={testId}>{value}</span>
  </div>
);

const useTotals = (lines, modes, shop, offers) => useMemo(() => {
  let net = 0; let vat = 0;
  Object.entries(lines).forEach(([code, q]) => {
    const p = shop.priceMap[code]; const i = shop.byCode[code];
    if (!p || !i) return;
    const normal = unitPrice(i, p, modes?.[code]);
    const o = offerFor(offers, code, modes?.[code], normal, q);
    const u = o ? o.offer_price : normal;
    net += q * u;
    vat += (q * u * i.vat_rate) / 100;
  });
  return { net: Math.round(net * 100) / 100, vat: Math.round(vat * 100) / 100 };
}, [lines, modes, shop, offers]);

export default function Cart() {
  const { t } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const cart = useCart();
  const shop = useShop();
  const delivery = useDelivery();
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null);
  const confirm = useConfirm();
  const offers = useOffers();
  const totals = useTotals(cart.lines, cart.modes, shop, offers);
  const approver = user.permissions.includes("orders:approve") || delivery.data?.require_approval === false;
  const slots = delivery.data?.slots;

  useEffect(() => {
    if (slots && cart.date && !slots.some((s) => s.date === cart.date)) cart.setMeta({ date: null });
    else if (slots?.length && !cart.date) cart.setMeta({ date: slots[0].date });
  }, [slots, cart]);

  const submit = async () => {
    if (!cart.date) { toast.error(t("checkout.pickDate")); return; }
    setBusy(true);
    const localId = cart.ensureLocalId();
    try {
      const lines = Object.entries(cart.lines).map(([item_code, qty_boxes]) => ({ item_code, qty_boxes, uom: cart.modeOf(item_code) }));
      const { data } = await api.post("/orders", { local_id: localId, lines, requested_delivery_date: cart.date, notes: cart.notes || null });
      cart.clear();
      setPlaced(data);
      qc.invalidateQueries({ queryKey: ["orders"] });
    } catch (e) {
      const d = errorDetail(e);
      if (d === "delivery_date_invalid") { cart.setMeta({ date: null }); delivery.refetch(); toast.error(t("checkout.dateInvalid")); }
      else if (String(d).startsWith("items_unavailable")) toast.error(t("checkout.itemsUnavailable"));
      else toast.error(t("checkout.failed"));
    } finally { setBusy(false); }
  };

  if (placed) return <OrderPlaced result={placed} />;
  const clearCart = async () => {
    if (await confirm({ title: t("shop.clearCartTitle"), body: t("shop.clearCartBody"), danger: true, confirmLabel: t("shop.clearCart") })) { cart.clear(); toast.success(t("shop.cartCleared")); }
  };
  if (shop.isLoading) return <SkeletonBlock className="h-96" />;
  if (!cart.count) {
    return <EmptyState icon={ShoppingCart} title={t("shop.cartEmpty")} sub={t("shop.cartEmptySub")} testId="cart-empty"
      action={<Link to="/app/products" data-testid="cart-browse" className="flex h-12 items-center rounded-full bg-brand px-6 text-sm font-semibold text-navy">{t("shop.browse")}</Link>} />;
  }

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_380px]" data-testid="cart-page">
      <section className="min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-bold text-navy sm:text-3xl">{t("checkout.title")} <span className="text-base font-medium text-slate-500">· {t("checkout.itemsCount", { n: cart.count })}</span></h1>
          <button type="button" onClick={clearCart} className="flex h-10 items-center gap-1.5 rounded-full border border-rose-200 px-3 text-sm font-semibold text-rose-700 hover:bg-rose-50" data-testid="cart-clear-button"><Trash2 className="h-4 w-4" /> {t("shop.clearCart")}</button>
        </div>
        <ul className="mt-3 divide-y divide-slate-100 rounded-2xl bg-white px-4 shadow-sm" data-testid="cart-lines">
          {Object.entries(cart.lines).map(([code, q]) => <CartLine key={code} item={shop.byCode[code]} price={shop.priceMap[code]} qty={q} onQty={(n) => cart.setQty(code, n)} mode={cart.modeOf(code)} onMode={(m) => cart.setMode(code, m)} />)}
        </ul>
      </section>
      <aside className="min-w-0 space-y-4 lg:sticky lg:top-20 lg:self-start">
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-base font-semibold text-navy md:text-lg">{t("checkout.deliveryDate")}</h2>
          <DeliveryPicker delivery={delivery} value={cart.date} onChange={(date) => cart.setMeta({ date })} />
          <label className="mt-4 block text-sm font-medium text-navy" htmlFor="cart-notes">{t("checkout.notes")}</label>
          <textarea id="cart-notes" rows={2} maxLength={500} value={cart.notes} onChange={(e) => cart.setMeta({ notes: e.target.value })} placeholder={t("checkout.notesPh")} data-testid="cart-notes-input"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-base text-navy outline-none focus:border-brand focus:ring-4 focus:ring-brand/15" />
        </div>
        <div className="space-y-2 rounded-2xl bg-white p-4 shadow-sm" data-testid="cart-summary">
          <Row label={t("checkout.net")} value={fmtMoney(totals.net)} testId="cart-net" />
          <Row label={t("checkout.vat")} value={fmtMoney(totals.vat)} testId="cart-vat" />
          <div className="border-t border-slate-100 pt-2"><Row strong label={t("checkout.gross")} value={fmtMoney(totals.net + totals.vat)} testId="cart-gross" /></div>
          {!approver && <p className="pt-1 text-xs text-slate-500" data-testid="approval-hint">{t("checkout.approvalHint")}</p>}
          <button type="button" onClick={submit} disabled={busy || !slots?.length} data-testid="cart-submit-button"
            className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand text-base font-semibold text-white transition-transform duration-150 active:scale-[0.98] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t(approver ? "checkout.submit" : "checkout.submitApproval")}
          </button>
        </div>
      </aside>
    </div>
  );
}
